import React, { useRef, useState } from 'react';
import { CheckCircle2, MapPin, MessageCircle, Minus, PackageSearch, Plus, Send, ShieldCheck, ShoppingCart, Trash2, Truck, Package } from 'lucide-react';
import { DEPARTAMENTOS, destinoEnvio } from '../../dominio/logicaEntrega';
import { operarOrden, prepararLlamadaDirecta } from '../../servicios/ordenServicio';
import { construirUrlWhatsApp } from '../../dominio/logicaMensajes';
import { esCobroAutomatico, nombrePasarela } from '../../dominio/pasarelas';
import { guardarDetallePedido, guardarPedidoLocal, resumenPedido } from '../../utilidades/pedidosLocales';
import {
  ESTADOS_PEDIDO_PUBLICO, MAX_LINEAS_PEDIDO, MAX_UNIDADES_LINEA, mediosPagoTienda, pedidoTerminado, solesDeCentimos, stockDeSeleccion,
} from '../../dominio/logicaTiendaPublica';
import { useSenalPedido } from '../../hooks/useSenalPedido';
import { EstadoPedidoPublico } from './tiendaPublica/EstadoPedidoPublico';
import { LogosPago } from './tiendaPublica/LogosPago';

/** Compatibilidad: etiquetas de estado para otros componentes. */
export const ESTADOS_PEDIDO = ESTADOS_PEDIDO_PUBLICO;

const mensajeError = (e) => (e?.message && !/^internal$/i.test(e.message) ? e.message : 'No se pudo completar. Revisa tu conexión e inténtalo de nuevo.');

// El servidor valida precio y stock con el producto real: si el catálogo que ve el
// comprador quedó viejo (el gimnasio cambió un precio o vendió), se recarga.
const CATALOGO_DESACTUALIZADO = /Actualiza el catálogo|Ajusta tu carrito|Quítalo de tu carrito/i;

export const CarritoCatalogo = ({ gymId, catalogo, cantidades, variantes = {}, cambiarCantidad, onCatalogoDesactualizado }) => {
  const [cliente, setCliente] = useState({ nombre: '', telefono: '', dni: '', email: '', entrega: 'retiro', direccion: '' });
  // El servidor exige el correo si la pasarela lo necesita (Culqi), aunque el catálogo en caché no lo sepa aún.
  const [correoExigido, setCorreoExigido] = useState(false);
  // Envío a provincia: destino y agencia (el DNI de «Tus datos» es el de quien recoge).
  const [envio, setEnvio] = useState({ departamento: '', provincia: '', distrito: '', agencia: '' });
  const agencias = Array.isArray(catalogo.agenciasEnvio) ? catalogo.agenciasEnvio : [];
  const [preferenciaComprobante, setPreferenciaComprobante] = useState('interno');
  const [receptorFiscal, setReceptorFiscal] = useState({ numeroDocumento: '', nombre: '', direccion: '', email: '' });
  const claveResultado = `norgym_pedido_resultado_${gymId}`;
  const [resultado, setResultado] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(claveResultado) || 'null'); } catch { return null; }
  }), [error, setError] = useState(''), [ocupado, setOcupado] = useState(false);
  const enCurso = useRef(false);
  // Carrito tal como se envió en el último pedido: solo un cambio posterior cuenta como carrito nuevo.
  // (Al abrir con un pedido ya guardado, cuenta el carrito de ese momento.)
  const carritoDelPedido = useRef(resultado ? JSON.stringify(cantidades) : null);
  const claveStorage = `norgym_pedido_pendiente_${gymId}`;
  const [pendiente, setPendiente] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(claveStorage) || 'null'); } catch { return null; }
  });
  const pasarela = esCobroAutomatico(catalogo.modoCobro);
  const pagoPrueba = catalogo.modoCobro === 'automatico_pruebas';
  const nombrePago = nombrePasarela(catalogo.proveedorCobro || 'mercadopago');
  const medios = mediosPagoTienda({ automatico: pasarela, proveedor: catalogo.proveedorCobro || 'mercadopago', metodosPago: catalogo.metodosPago });
  const pideCorreo = correoExigido || (pasarela && catalogo.proveedorCobro === 'culqi');


  const lineas = Object.entries(cantidades).map(([clave, cantidad]) => {
    const seleccion = variantes[clave] || {};
    const producto = catalogo.productos.find(p => p.id === (seleccion.productoId || clave));
    if (!producto) return null;
    const linea = { ...producto, clave, cantidad, varianteId: seleccion.varianteId || '',
      talla: seleccion.talla || '', color: seleccion.color || '', skuVariante: seleccion.skuVariante || '' };
    return { ...linea, stockLinea: stockDeSeleccion(producto, linea) };
  }).filter(Boolean);
  const stockCambio = lineas.length !== Object.keys(cantidades).length || lineas.some(p => p.cantidad > p.stockLinea);
  const estimado = lineas.reduce((s, p) => s + Math.round(p.precio * 100) * p.cantidad, 0);
  const unidades = lineas.reduce((s, p) => s + p.cantidad, 0);

  const guardarResultado = (res) => {
    setResultado(res);
    // «Mis pedidos» de este celular: la clave privada y el último detalle quedan aquí.
    if (res?.claveConsulta) {
      guardarPedidoLocal(gymId, resumenPedido(res.claveConsulta, res));
      guardarDetallePedido(gymId, res.claveConsulta, res);
    }
    try { sessionStorage.setItem(claveResultado, JSON.stringify(res)); } catch { /* sin almacenamiento */ }
  };

  const enviar = async event => {
    event.preventDefault();
    if (enCurso.current) return;
    if (!pendiente && (!lineas.length || lineas.length > MAX_LINEAS_PEDIDO)) { setError(`Tu pedido debe tener entre 1 y ${MAX_LINEAS_PEDIDO} productos distintos.`); return; }
    if (!pendiente && stockCambio) { setError('Cambió la disponibilidad. Ajusta o quita los productos marcados.'); return; }
    enCurso.current = true; setOcupado(true); setError('');
    let solicitud = pendiente;
    try {
      if (!solicitud) {
        solicitud = { accion: 'crear', gymId, ...cliente, ...(cliente.entrega === 'provincia' ? envio : {}), preferenciaComprobante,
          receptorFiscal: preferenciaComprobante === 'factura' ? { ...receptorFiscal, tipoDocumento: 'RUC' }
            : preferenciaComprobante === 'boleta' ? { direccion: receptorFiscal.direccion, email: receptorFiscal.email } : {},
          idempotencyKey: window.crypto.randomUUID(), totalEsperadoCentimos: estimado,
          items: lineas.map(p => ({ productoId: p.id, cantidad: p.cantidad, varianteId: p.varianteId, talla: p.talla, color: p.color })) };
        sessionStorage.setItem(claveStorage, JSON.stringify(solicitud));
        setPendiente(solicitud);
      }
      // Copia local de lo pedido para mostrarlo en la confirmación (0 lecturas).
      const lineasPedido = lineas.map(p => ({ nombre: p.nombre, cantidad: p.cantidad, talla: p.talla, color: p.color,
        subtotalCentimos: Math.round(p.precio * 100) * p.cantidad }));
      const res = { ...await operarOrden(solicitud), claveConsulta: solicitud.idempotencyKey,
        compradorMensaje: { nombre: solicitud.nombre, dni: solicitud.dni || '', entrega: solicitud.entrega,
          ...(solicitud.entrega === 'provincia' ? { destino: destinoEnvio({ tipo: 'provincia', ...solicitud }) } : {}) },
        ...(lineasPedido.length ? { lineasPedido } : {}) };
      carritoDelPedido.current = JSON.stringify(cantidades);
      guardarResultado(res);
      sessionStorage.removeItem(claveStorage); setPendiente(null);
      Object.keys(cantidades).forEach(id => cambiarCantidad(id, 0));
    } catch (e) {
      const precioCambio = /precio cambió/i.test(e?.message || '');
      setError(precioCambio ? 'Cambió el precio de un producto. Ya actualizamos la tienda: revisa el nuevo total y vuelve a enviar tu pedido.' : mensajeError(e));
      if (CATALOGO_DESACTUALIZADO.test(e?.message || '')) onCatalogoDesactualizado?.();
      if (/correo/i.test(e?.message || '')) setCorreoExigido(true);
      if (['functions/invalid-argument', 'functions/failed-precondition', 'functions/resource-exhausted'].includes(e.code)) {
        sessionStorage.removeItem(claveStorage); setPendiente(null);
      }
    } finally { enCurso.current = false; setOcupado(false); }
  };

  /** Une la respuesta pública con lo que solo existe en este navegador. */
  const fusionar = (res) => ({ ...res, claveConsulta: resultado.claveConsulta,
    ...(resultado.compradorMensaje ? { compradorMensaje: resultado.compradorMensaje } : {}),
    ...(resultado.lineasPedido ? { lineasPedido: resultado.lineasPedido } : {}),
    ...(!res.cobroManual && resultado.cobroManual ? { cobroManual: resultado.cobroManual } : {}),
    ...(!res.qrYape && resultado.qrYape ? { qrYape: resultado.qrYape } : {}) });

  const consultar = async (accion = 'consultar_publico') => {
    if (enCurso.current || !resultado?.claveConsulta) return;
    enCurso.current = true; setOcupado(true); setError('');
    try {
      const datos = { accion, gymId, idempotencyKey: resultado.claveConsulta };
      // Los datos de cobro ya guardados no se vuelven a pedir (ahorra la lectura del gym).
      if (accion === 'consultar_publico' && (resultado.cobroManual || resultado.qrYape)) datos.cobroEnCache = true;
      guardarResultado(fusionar(await operarOrden(datos)));
    } catch (e) { setError(mensajeError(e)); }
    finally { enCurso.current = false; setOcupado(false); }
  };
  // El gimnasio cambió el pedido (confirmó el pago, lo despachó…): se trae una vez.
  useSenalPedido(resultado?.claveConsulta && !pedidoTerminado(resultado.estado) ? resultado.claveConsulta : null,
    resultado?.version, () => consultar());

  const mensajePedido = resultado ? [
    `Hola ${catalogo.nombre}, he realizado el pedido número ${resultado.numeroPedido || resultado.id}.`,
    resultado.compradorMensaje?.nombre && `Nombre: ${resultado.compradorMensaje.nombre}.`,
    resultado.compradorMensaje?.dni && `DNI: ${resultado.compradorMensaje.dni}.`,
    `Total de productos: ${solesDeCentimos(resultado.totalCentimos)}.`,
    resultado.compradorMensaje?.entrega && `Entrega solicitada: ${resultado.compradorMensaje.entrega === 'provincia'
      ? `envío a provincia (${resultado.compradorMensaje.destino || 'por coordinar'})`
      : resultado.compradorMensaje.entrega === 'delivery' ? 'envío a domicilio' : 'recojo en tienda'}.`,
    resultado.pagoReportado && 'Ya pagué; te envío la captura.',
    esCobroAutomatico(resultado.modoCobro)
      ? `Mi pedido usa ${nombrePasarela(resultado.proveedorCobro || 'mercadopago')}. Quisiera consultar su estado y coordinar la entrega; verifica el pago en el panel.`
      : 'Quisiera coordinar la entrega.'
  ].filter(Boolean).join('\n') : '';

  // La confirmación del último pedido se muestra mientras el carrito esté vacío. Si el
  // comprador agrega productos nuevos, se ve su carrito (con un aviso del pedido anterior).
  const carritoNuevo = Object.keys(cantidades).length > 0 && JSON.stringify(cantidades) !== carritoDelPedido.current;
  if (resultado && !carritoNuevo) {
    return <section className="tp-confirmacion" id="mi-pedido" aria-live="polite">
      <div className="tp-confirmacion-cabecera">
        <span className="tp-confirmacion-icono" aria-hidden="true"><CheckCircle2 size={28} /></span>
        <div><h2>Pedido recibido</h2><p>Pedido N.º {resultado.numeroPedido || resultado.id}</p></div>
      </div>

      <EstadoPedidoPublico gymId={gymId} resultado={resultado} metodosPago={catalogo.metodosPago} ocupado={ocupado} nombreTienda={catalogo.nombre} whatsapp={catalogo.whatsapp}
        onRecuperarPago={() => consultar('mp_recuperar_checkout_publico')}
        onActualizado={(res) => guardarResultado(fusionar(res))} />

      {resultado.claveConsulta && !pedidoTerminado(resultado.estado) && <p className="tp-nota tp-nota--centro" role="status">
        {ocupado ? 'Actualizando tu pedido…' : 'Esta página se actualiza sola cuando el gimnasio avance tu pedido.'}</p>}
      {error && <p role="alert" className="tp-error">{error}</p>}

      <div className="tp-seguimiento-privado">
        <a className="tp-boton-texto" href={`/catalogo/${gymId}/pedidos`}><PackageSearch size={16} aria-hidden="true" /> Ver mis pedidos</a>
        <p className="tp-nota">Tu pedido quedó guardado en «Mis pedidos» de este celular. Desde otro celular, búscalo ahí con tu DNI y tu número de WhatsApp.</p>
      </div>

      <a className="tp-boton tp-boton--whatsapp tp-boton--ancho" target="_blank" rel="noreferrer"
        href={construirUrlWhatsApp(catalogo.whatsapp, mensajePedido, '')}><MessageCircle size={18} aria-hidden="true" /> Abrir WhatsApp del gimnasio</a>
      <p className="tp-nota tp-nota--centro">Se abre el chat con el mensaje listo; pulsa Enviar en WhatsApp.</p>
      <button type="button" className="tp-boton-texto tp-boton-texto--centro" disabled={ocupado} onClick={() => {
        sessionStorage.removeItem(claveResultado);
        sessionStorage.removeItem(claveStorage); setPendiente(null);
        setResultado(null);
      }}>Hacer otro pedido</button>
    </section>;
  }

  return <section className="tp-carrito" id="mi-pedido" aria-labelledby="tp-carrito-titulo">
    {resultado && <p className="tp-aviso tp-aviso--info tp-pedido-anterior" role="status">
      <PackageSearch size={16} aria-hidden="true" />
      <span>Tu pedido N.º {resultado.numeroPedido || resultado.id} quedó en <a href={`/catalogo/${gymId}/pedidos`}>Mis pedidos</a>. Este es un pedido nuevo.</span>
    </p>}
    <div className="tp-carrito-cabecera">
      <h2 id="tp-carrito-titulo">Tu carrito</h2>
      <span>{unidades} {unidades === 1 ? 'unidad' : 'unidades'}</span>
    </div>
    {!lineas.length && !Object.keys(cantidades).length && <div className="tp-carrito-vacio">
      <ShoppingCart size={32} aria-hidden="true" /><p><strong>Tu carrito está vacío</strong>Agrega productos para armar tu pedido.</p>
    </div>}
    {stockCambio && !pendiente && <p role="alert" className="tp-error">Cambió la disponibilidad. Ajusta o quita los productos marcados.</p>}
    <ul className="tp-carrito-lineas">
      {Object.keys(cantidades).filter(clave => !lineas.some(p => p.clave === clave)).map(clave =>
        <li key={clave} className="tp-carrito-linea is-retirado"><span>Producto retirado del catálogo</span>
          <button type="button" className="tp-icono-boton" disabled={ocupado || !!pendiente} onClick={() => cambiarCantidad(clave, 0)} aria-label="Quitar producto retirado"><Trash2 size={16} aria-hidden="true" /></button></li>)}
      {lineas.map(p => {
        const maximo = Math.min(MAX_UNIDADES_LINEA, p.stockLinea);
        const excede = p.cantidad > p.stockLinea;
        return <li className={`tp-carrito-linea${excede ? ' is-excede' : ''}`} key={p.clave}>
          <div className="tp-carrito-linea-info">
            <strong>{p.nombre}</strong>
            {(p.talla || p.color) && <small>{[p.talla && `Talla: ${p.talla}`, p.color && `Color: ${p.color}`].filter(Boolean).join(' · ')}</small>}
            <span className="tp-carrito-unitario">{solesDeCentimos(Math.round(p.precio * 100))} c/u</span>
            {excede && <span className="tp-carrito-aviso">{p.stockLinea > 0 ? `Solo quedan ${p.stockLinea}` : 'Sin stock'}</span>}
          </div>
          <div className="tp-carrito-linea-acciones">
            <div className="tp-cantidad tp-cantidad--chica" role="group" aria-label={`Cantidad de ${p.nombre}`}>
              <button type="button" aria-label={`Restar una unidad de ${p.nombre}`} disabled={ocupado || !!pendiente || p.cantidad <= 1}
                onClick={() => cambiarCantidad(p.clave, p.cantidad - 1)}><Minus size={14} aria-hidden="true" /></button>
              <output aria-live="polite">{p.cantidad}</output>
              <button type="button" aria-label={`Sumar una unidad de ${p.nombre}`} disabled={ocupado || !!pendiente || p.cantidad >= maximo}
                onClick={() => cambiarCantidad(p.clave, p.cantidad + 1)}><Plus size={14} aria-hidden="true" /></button>
            </div>
            <strong className="tp-carrito-subtotal">{solesDeCentimos(Math.round(p.precio * 100) * p.cantidad)}</strong>
            <button type="button" className="tp-icono-boton" aria-label={['Quitar', p.nombre, p.talla, p.color].filter(Boolean).join(' ')}
              disabled={ocupado || !!pendiente} onClick={() => cambiarCantidad(p.clave, 0)}><Trash2 size={16} aria-hidden="true" /></button>
          </div>
        </li>;
      })}
    </ul>

    {(lineas.length > 0 || pendiente) && <>
      <dl className="tp-resumen">
        <div><dt>Subtotal</dt><dd>{solesDeCentimos(estimado)}</dd></div>
        <div><dt>Entrega</dt><dd>{cliente.entrega === 'provincia' ? 'Flete en destino o por coordinar' : cliente.entrega === 'delivery' ? 'Por coordinar' : 'Gratis, retiro en tienda'}</dd></div>
        <div className="tp-resumen-total"><dt>Total referencial</dt><dd>{solesDeCentimos(estimado)}</dd></div>
      </dl>
      {pendiente && <p className="tp-aviso tp-aviso--atencion" role="status"><span>Hay un pedido sin confirmar. Usa el mismo botón para recuperarlo sin duplicarlo.</span></p>}

      <form className="tp-checkout" onSubmit={enviar} onFocus={() => prepararLlamadaDirecta()} onInput={() => prepararLlamadaDirecta()}>
        <fieldset disabled={ocupado || !!pendiente}>
          <legend>¿Cómo lo recibes?</legend>
          <div className="tp-entregas">
            <label className={cliente.entrega === 'retiro' ? 'is-activo' : ''}>
              <input type="radio" name="entrega" value="retiro" checked={cliente.entrega === 'retiro'}
                onChange={e => setCliente({ ...cliente, entrega: e.target.value, direccion: '' })} />
              <MapPin size={18} aria-hidden="true" /><span><strong>Retiro en tienda</strong><small>{catalogo.direccion || 'En la sede del gimnasio'}</small></span>
            </label>
            {catalogo.deliveryHabilitado && <label className={cliente.entrega === 'delivery' ? 'is-activo' : ''}>
              <input type="radio" name="entrega" value="delivery" checked={cliente.entrega === 'delivery'}
                onChange={e => setCliente({ ...cliente, entrega: e.target.value })} />
              <Truck size={18} aria-hidden="true" /><span><strong>Delivery</strong><small>{catalogo.notaDelivery || 'Tarifa y cobertura por coordinar'}</small></span>
            </label>}
            {catalogo.envioProvinciaHabilitado && <label className={cliente.entrega === 'provincia' ? 'is-activo' : ''}>
              <input type="radio" name="entrega" value="provincia" checked={cliente.entrega === 'provincia'}
                onChange={e => { setCliente({ ...cliente, entrega: e.target.value, direccion: '' }); if (agencias.length === 1) setEnvio(v => ({ ...v, agencia: agencias[0] })); }} />
              <Package size={18} aria-hidden="true" /><span><strong>Envío a provincia</strong>
                <small>{catalogo.notaEnvio || (agencias.length ? `Por ${agencias.join(', ')}. Flete en destino.` : 'Por agencia, flete en destino.')}</small></span>
            </label>}
          </div>
        </fieldset>
        <fieldset disabled={ocupado || !!pendiente}>
          <legend>Tus datos</legend>
          <label className="tp-campo"><span>Nombre completo</span><input required value={cliente.nombre} maxLength={120} autoComplete="name"
            placeholder="Nombres y apellidos" onChange={e => setCliente({ ...cliente, nombre: e.target.value })} /></label>
          <div className="tp-campos-dobles">
            <label className="tp-campo"><span>WhatsApp</span><input required type="tel" inputMode="tel" autoComplete="tel-national"
              pattern="(51)?9[0-9]{8}" title="Celular de 9 dígitos que empieza con 9" value={cliente.telefono} placeholder="987654321"
              onChange={e => setCliente({ ...cliente, telefono: e.target.value.replace(/\s/g, '') })} /></label>
            <label className="tp-campo"><span>DNI</span><input required inputMode="numeric" pattern="[0-9]{8}" maxLength={8}
              title="8 dígitos" value={cliente.dni} placeholder="12345678" onChange={e => setCliente({ ...cliente, dni: e.target.value.replace(/[^0-9]/g, '') })} /></label>
          </div>
          {(pasarela || pideCorreo) && <label className="tp-campo"><span>{pideCorreo ? 'Correo' : 'Correo (opcional)'}</span><input type="email" required={pideCorreo}
            maxLength={120} autoComplete="email" value={cliente.email} placeholder="tucorreo@ejemplo.com"
            onChange={e => setCliente({ ...cliente, email: e.target.value.trim() })} /></label>}
          {cliente.entrega === 'provincia' && <>
            <div className="tp-campos-dobles">
              <label className="tp-campo"><span>Departamento</span><select required value={envio.departamento}
                onChange={e => setEnvio({ ...envio, departamento: e.target.value })}>
                <option value="">Selecciona</option>{DEPARTAMENTOS.map(d => <option key={d} value={d}>{d}</option>)}
              </select></label>
              <label className="tp-campo"><span>Provincia</span><input required maxLength={60} value={envio.provincia} placeholder="Ej. Arequipa"
                onChange={e => setEnvio({ ...envio, provincia: e.target.value })} /></label>
            </div>
            <div className="tp-campos-dobles">
              <label className="tp-campo"><span>Distrito (opcional)</span><input maxLength={60} value={envio.distrito}
                onChange={e => setEnvio({ ...envio, distrito: e.target.value })} /></label>
              <label className="tp-campo"><span>Agencia</span>{agencias.length
                ? <select required value={envio.agencia} onChange={e => setEnvio({ ...envio, agencia: e.target.value })}>
                  <option value="">Selecciona</option>{agencias.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                : <input required maxLength={40} value={envio.agencia} placeholder="Ej. Shalom" onChange={e => setEnvio({ ...envio, agencia: e.target.value })} />}</label>
            </div>
            <label className="tp-campo"><span>Sede de la agencia o dirección (opcional)</span><input maxLength={200} value={cliente.direccion}
              placeholder="Ej. Shalom Av. Ejército" onChange={e => setCliente({ ...cliente, direccion: e.target.value })} /></label>
            <p className="tp-nota-envio">La agencia entrega el paquete con el DNI que indicaste. El flete se paga en destino o se coordina con el gimnasio.</p>
          </>}
          {cliente.entrega === 'delivery' && <label className="tp-campo"><span>Dirección de entrega</span><input required maxLength={300}
            autoComplete="street-address" value={cliente.direccion} placeholder="Distrito, calle y referencia"
            onChange={e => setCliente({ ...cliente, direccion: e.target.value })} /></label>}
          <label className="tp-campo"><span>Comprobante solicitado</span><select value={preferenciaComprobante} onChange={e => setPreferenciaComprobante(e.target.value)}>
            <option value="interno">Ticket del gimnasio</option><option value="boleta">Boleta de venta</option><option value="factura">Factura</option>
          </select></label>
          {preferenciaComprobante === 'factura' && <div className="tp-campos-dobles">
            <label className="tp-campo"><span>RUC</span><input required inputMode="numeric" pattern="[0-9]{11}" maxLength={11} value={receptorFiscal.numeroDocumento}
              onChange={e => setReceptorFiscal({ ...receptorFiscal, numeroDocumento: e.target.value })} /></label>
            <label className="tp-campo"><span>Razón social</span><input required maxLength={200} value={receptorFiscal.nombre}
              onChange={e => setReceptorFiscal({ ...receptorFiscal, nombre: e.target.value })} /></label>
          </div>}
          {preferenciaComprobante !== 'interno' && <>
            <label className="tp-campo"><span>{preferenciaComprobante === 'factura' ? 'Dirección fiscal' : 'Dirección del receptor (opcional)'}</span><input required={preferenciaComprobante === 'factura'}
              maxLength={300} value={receptorFiscal.direccion} onChange={e => setReceptorFiscal({ ...receptorFiscal, direccion: e.target.value })} /></label>
            <label className="tp-campo"><span>Correo para el comprobante (opcional)</span><input type="email" maxLength={200} autoComplete="email"
              value={receptorFiscal.email} onChange={e => setReceptorFiscal({ ...receptorFiscal, email: e.target.value })} /></label>
          </>}
          <p className="tp-nota">El gimnasio confirma los datos del comprobante antes del cobro; pedirlo no lo emite.</p>
        </fieldset>
        <button className="tp-boton tp-boton--primario tp-boton--ancho tp-boton--grande" disabled={ocupado || (!pendiente && (!lineas.length || stockCambio))} type="submit">
          <Send size={18} aria-hidden="true" />
          {ocupado ? 'Enviando…' : pendiente ? 'Recuperar pedido' : `${pasarela ? 'Reservar y pagar' : 'Enviar pedido'} · ${solesDeCentimos(estimado)}`}
        </button>
        <p className="tp-nota tp-nota--centro">Tus datos se usan solo para atender este pedido. <a href="/legal/privacidad" target="_blank" rel="noopener noreferrer">Política de privacidad</a></p>
        {error && <p role="alert" className="tp-error">{error}</p>}
      </form>
      <p className="tp-seguridad"><ShieldCheck size={17} aria-hidden="true" />
        <span>{pasarela
          ? `Al enviar, el gimnasio reserva tu stock y pagas en línea con ${nombrePago}${pagoPrueba ? ' (modo de pruebas)' : ''}. El precio y el stock se validan antes de cobrar.`
          : 'Enviar el pedido no cobra ni reserva stock: el gimnasio confirma disponibilidad y total y te avisa cómo pagar.'}</span></p>
      {medios.length > 0 && <div className="tp-metodos"><span aria-hidden="true">Medios de pago aceptados</span><LogosPago medios={medios} /></div>}
    </>}
  </section>;
};
